import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { NodeAvatar, EmptyState, Pill } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import { BulkActionBar, BulkCheckbox } from "./BulkActionBar";
import { getAgentPersona } from "./CockpitHeader";
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useBulkSelect } from "../../hooks/use-bulk-select";
import { MoveToModal } from "../scope/MoveToModal";
import { ArchiveModal } from "../scope/ArchiveModal";
import type { LibraryNode } from "../../../lib/types";

interface AgentsPanelProps {
  /** Node whose direct children we list (agent id, or root id on the workspace cockpit). */
  nodeId: string;
  /** True on the workspace cockpit — titles the panel "Agents" and tunes empty-state copy. */
  isRoot?: boolean;
  /**
   * Sprint 5w: when set, the panel renders nothing if there are no sub-agents.
   * Folder cockpits pass this — a folder surfaces sub-agents only when some
   * exist; otherwise the panel disappears entirely (creating the first sub-agent
   * for an empty folder lives in the header, not in an empty-state pull here).
   */
  hideWhenEmpty?: boolean;
}

function isActiveAgent(node: LibraryNode): boolean {
  return node.status === "active" || (!!node.agent_config && node.status !== "idle");
}

export function AgentsPanel({ nodeId, isRoot, hideWhenEmpty }: AgentsPanelProps) {
  const { currentWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const [moveOpen, setMoveOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  // Sprint 23.5 — "new (sub-)agent" navigates to the Hire wizard with the
  // current node pre-filled as the parent (reports_to).
  const openHire = () => navigate(`/hire?parent_id=${encodeURIComponent(nodeId)}`);

  // Sprint 5w: this panel is agents-only. Folders are organisation, not
  // personas — they render in FilesPanel ("Folders & files"). Mixing them here
  // (the 5u shortcut) broke the user's mental model, so the filter is hardcoded
  // to type === "agent" with no folder branching.
  const children = useMemo(() => {
    return (tree.data ?? [])
      .filter((n) => n.parent_id === nodeId && n.type === "agent")
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  }, [tree.data, nodeId]);

  const bulk = useBulkSelect(children);
  const newButtonLabel = isRoot ? "New agent" : "New sub-agent";
  // Workspace cockpit → "Agents"; agent and folder cockpits → "Sub-agents".
  const panelTitle = isRoot ? "Agents" : "Sub-agents";

  // Folder cockpits with no sub-agents render nothing at all (Sprint 5w).
  if (hideWhenEmpty && children.length === 0) return null;

  const action = bulk.active ? undefined : children.length > 0 ? (
    <button onClick={() => openHire()} style={newButtonStyle}>
      <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
      {newButtonLabel}
    </button>
  ) : undefined;

  return (
    <>
      {bulk.active && (
        <BulkActionBar
          count={bulk.count}
          onMove={() => setMoveOpen(true)}
          onArchive={() => setArchiveOpen(true)}
          onCancel={bulk.clear}
        />
      )}
      <Panel
        title={panelTitle}
        count={children.length}
        action={action}
        isLoading={tree.isLoading}
        error={tree.error}
        onRetry={() => tree.refetch()}
        isEmpty={children.length === 0}
        empty={
          <EmptyState>
            <div>{isRoot ? "No agents yet." : "No sub-agents yet."}</div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 10 }}>
              <button onClick={() => openHire()} style={newButtonStyle}>
                <i className="ti ti-plus" style={{ fontSize: 13 }} aria-hidden="true" />
                {newButtonLabel}
              </button>
            </div>
          </EmptyState>
        }
      >
        {children.map((child) => (
          <PanelRow
            key={child.id}
            onClick={() => (bulk.active ? bulk.toggle(child.id) : navigate(`/agents/${child.id}`))}
          >
            <BulkCheckbox
              checked={bulk.isSelected(child.id)}
              visible={bulk.active}
              onToggle={(e) => bulk.toggle(child.id, e)}
              label={`Select ${child.name}`}
            />
            <NodeAvatar
              type={child.type}
              persona={child.type === "agent" ? getAgentPersona(child) : undefined}
              size="sm"
              name={child.name}
            />
            <span style={nameStyle}>{child.name}</span>
            {child.type === "agent" && isActiveAgent(child) && <Pill variant="teal">active</Pill>}
          </PanelRow>
        ))}
      </Panel>

      <MoveToModal
        open={moveOpen}
        onClose={() => setMoveOpen(false)}
        sources={bulk.selectedItems}
        onMoved={() => bulk.clear()}
      />
      <ArchiveModal
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        nodes={bulk.selectedItems}
        onArchived={() => bulk.clear()}
      />
    </>
  );
}

const newButtonStyle = {
  fontSize: 12,
  padding: "4px 10px",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap" as const,
};

const nameStyle = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};
